import { useAbsences } from '@hooks/useAbsences'
import { useDayLocks } from '@hooks/useDayLocks'
import { useEmployee } from '@hooks/useEmployee'
import { useTimeCards } from '@hooks/useTimeCards'
import { useWeekScope } from '@hooks/useWeekScope'
import { WeekScopeProvider } from 'providers/WeekScopeProvider'
import { dateIntersectsAbsence, isLeave } from '@utils/absence'
import { getDayOfWeek } from '@utils/dates'
import { DateTime } from 'luxon'
import { useMemo, createContext, useContext, ReactNode } from 'react'
import { useUserId } from '../hooks/useUserId'
import {
  TimeCardSubmissionBlockedReason,
  getTimeCardSubmissionBlockedReason,
  totalEntriesDuration,
  totalWorkedInSeconds as getTotalWorkedInSeconds,
} from 'utils/timeCards'
import { useModal } from '@hooks/useModal'
import { NoteForm } from '../components/header/NoteForm'
import { Trans, useTranslation } from 'react-i18next'
import { InfoCard } from 'components/containers/info-card'
import { useDateTimeToLocaleString } from '@hooks/useDateTimeWithLocale'
import { toast } from '@lib/toasts'
import {
  useSubmitTimeCard,
  useUnsubmitTimeCard,
} from '../hooks/useTimeCardManagement'
import { useEntries } from '@hooks/useEntries'
import * as Sentry from '@sentry/react'
import { useSaveCorrection } from '../hooks/useCorrections'
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PopoutLink } from 'components/links'

export type TimeCardsContextTimeCard = {
  absences: Absence[]
  active: boolean
  hasLeave: boolean
  lock?: DayLock
  numLabel: number
  scheduledWorkHours?: string
  scheduledWorkHoursInSeconds: number
  submit: () => Promise<void>
  unsubmit: () => Promise<void>
  saveCorrection: () => void
  totalAllocatedInSeconds: number
  totalTimeOff: number
  isInFuture: boolean
  isValid: boolean
  // Why this Time Card may not be submitted; undefined when it may be
  submissionBlockedReason?: TimeCardSubmissionBlockedReason
} & TimeCard

const defaultContext = {
  atLeastOneTimeCardSubmittable: false,
  end: DateTime.utc(),
  range: [] as DateTime[],
  start: DateTime.utc(),
  submitAll: async () => Promise.resolve(),
  totalHours: 0,
  timeCards: [] as TimeCardsContextTimeCard[],
  isLoading: false,
  isUpdating: false,
}

const TimeCardsContext = createContext(defaultContext)

export const TimeCardsContextProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const userId = useUserId()
  const weekScope = useWeekScope()
  const { end, range, start } = weekScope
  const { data: userAbsences = [] } = useAbsences({ userId, start, end })
  const { data: userEntries = [] } = useEntries({ userId, start, end })

  const modal = useModal()
  const { t } = useTranslation()

  const localize = useDateTimeToLocaleString()({
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })

  const employeeQuery = useEmployee({ userId, startDate: start })
  const timeCardsQuery = useTimeCards({ userId, start, end })
  const { data: dayLocks } = useDayLocks({ userId, start, end })
  const submitMutation = useSubmitTimeCard({ start, end })
  const unsubmitMutation = useUnsubmitTimeCard({ start, end })
  const saveCorrectionMutation = useSaveCorrection({ start, end })

  const isLoading = useMemo(
    () => timeCardsQuery.data === undefined || employeeQuery.data === undefined,
    [timeCardsQuery, employeeQuery],
  )

  const isUpdating = useMemo(
    () =>
      submitMutation.isLoading ||
      unsubmitMutation.isLoading ||
      saveCorrectionMutation.isLoading,
    [submitMutation, unsubmitMutation, saveCorrectionMutation],
  )

  const timeCards = useMemo(() => {
    let activeCardCount = 0
    const cards: TimeCardsContextTimeCard[] = []

    timeCardsQuery.data?.forEach((card) => {
      const active = card.id !== 0
      const numLabel = active ? activeCardCount + 1 : activeCardCount
      const scheduledHours =
        employeeQuery.data?.workSchedule[getDayOfWeek(card.date)]
      const absences = userAbsences.filter((absence) =>
        dateIntersectsAbsence(card.date, absence),
      )

      const entries = userEntries.filter(
        (entry) => entry.timeCard.id === card.id,
      )

      const timeOffInSeconds = absences.reduce(
        (sum, absence) =>
          // NOTE: We can ignore leave here because TCs may not be submitted on days with leave
          isLeave(absence) ? sum : sum + absence.duration,
        0,
      )

      const scheduledWorkHoursInSeconds =
        parseFloat(scheduledHours ?? '0') * 60 ** 2

      const hasLeave = absences.some(isLeave)

      const submissionBlockedReason = getTimeCardSubmissionBlockedReason({
        timeCard: card,
        entries,
        totalTimeOffInSeconds: timeOffInSeconds,
        onLeave: hasLeave,
        scheduledWorkHours: scheduledHours,
      })

      const isValid = submissionBlockedReason === undefined

      const isInFuture = card.date > DateTime.now()

      const totalAllocatedInSeconds = totalEntriesDuration(entries)

      const validateThenSubmit = () => {
        return new Promise<void>((resolve, reject) => {
          const submitTimeCard = async () => {
            try {
              await submitMutation.mutateAsync(card.id)

              toast({
                title: t('features.timeLogging.timeCardSubmitted'),
                content: t(
                  'features.timeLogging.timeCardSuccessfullySubmitted',
                  { date: localize(card.date) },
                ),
                variant: 'success',
              })
              resolve()
            } catch (e) {
              toast({
                title: t('features.timeLogging.errorSubmittingTimeCard'),
                content: t('features.timeLogging.timeCardCouldNotBeSubmitted'),
                variant: 'error',
              })
              Sentry.captureException(e)
              reject(e)
            }
          }

          const scheduledInSeconds = parseFloat(scheduledHours ?? '0') * 60 ** 2
          const totalWorkedInSeconds = getTotalWorkedInSeconds(card)

          const showShortWorkDayAlert =
            employeeQuery.data?.payType === 'hourly' &&
            scheduledInSeconds > totalWorkedInSeconds + timeOffInSeconds

          const showMissingBreakAlert = card.breaks.length === 0

          if (showShortWorkDayAlert || showMissingBreakAlert) {
            const id = modal.form({
              content: (
                // Modal content renders where the ModalProvider sits, above
                // this page's week scope, so hand the scope across the
                // boundary — NoteForm and its children both read from it.
                <WeekScopeProvider value={weekScope}>
                  <NoteForm
                    date={card.date}
                    onCancel={() => {
                      modal.close(id)
                      resolve()
                    }}
                    onSave={async () => {
                      await submitTimeCard()
                      modal.close(id)
                    }}
                    saveButtonText={t('common.submit')}
                  >
                    <div className="flex flex-col gap-2">
                      {showShortWorkDayAlert && (
                        <InfoCard variant="warning">
                          <Trans
                            i18nKey="features.timeLogging.confirmAllocatedTimeIsLessThanScheduled"
                            values={{ date: localize(card.date) }}
                            components={{ s: <strong /> }}
                          />
                        </InfoCard>
                      )}

                      {showMissingBreakAlert && (
                        <InfoCard variant="warning">
                          <Trans
                            i18nKey="features.timeLogging.confirmNoBreaksTaken"
                            values={{ date: localize(card.date) }}
                            components={{ s: <strong /> }}
                          />
                        </InfoCard>
                      )}
                    </div>

                    <label
                      className="-mb-2 text-xs text-neutral-500"
                      htmlFor="note"
                    >
                      {t('features.timeLogging.notesOptional')}
                    </label>
                  </NoteForm>
                </WeekScopeProvider>
              ),
              title: t('features.timeLogging.confirmSubmission'),
            })
          } else {
            void submitTimeCard()
          }
        })
      }

      const handleUnsubmit = async () => {
        try {
          await unsubmitMutation.mutateAsync(card.id)

          toast({
            title: t('features.timeLogging.timeCardUnsubmitted'),
            content: t('features.timeLogging.timeCardSuccessfullyUnsubmitted'),
            variant: 'default',
          })
        } catch (e) {
          toast({
            title: t('features.timeLogging.errorUnsubmittingTimeCard'),
            content: t('features.timeLogging.timeCardCouldNotBeUnsubmitted'),
            variant: 'error',
          })
          Sentry.captureException(e)
        }
      }

      const handleSaveCorrection = () => {
        modal.confirm({
          content: (
            <div className="flex flex-col gap-4 text-left">
              <p>{t('features.timeLogging.correctionsCannotBeDeleted')}</p>
              <div className="bg-warning-100 text-warning-800 flex gap-2 items-center border border-warning-200 p-2.5">
                <FontAwesomeIcon icon={faTriangleExclamation} />

                <div>
                  {t('features.timeLogging.correctionsAreNotSentToWorkday')}
                </div>
              </div>

              <p>
                <Trans
                  i18nKey="features.timeLogging.correctionShouldBeReviewedInQueue"
                  components={{
                    correctionsQueueLink: (
                      <PopoutLink to="/admin/corrections-queue" />
                    ),
                  }}
                />
              </p>
            </div>
          ),
          onConfirm: () => saveCorrectionMutation.mutate(card.id),
          title: t('features.timeLogging.saveCorrections'),
        })
      }

      cards.push({
        ...card,
        absences,
        active,
        hasLeave,
        isValid,
        isInFuture,
        numLabel,
        lock: dayLocks?.find((lock) => lock.date.hasSame(card.date, 'day')),
        submit: validateThenSubmit,
        unsubmit: handleUnsubmit,
        saveCorrection: handleSaveCorrection,
        scheduledWorkHours: scheduledHours,
        scheduledWorkHoursInSeconds,
        submissionBlockedReason,
        totalAllocatedInSeconds,
        totalTimeOff: timeOffInSeconds,
      })

      activeCardCount = numLabel
    })

    return cards
  }, [
    dayLocks,
    employeeQuery.data,
    localize,
    modal,
    submitMutation,
    unsubmitMutation,
    saveCorrectionMutation,
    t,
    timeCardsQuery.data,
    userAbsences,
    userEntries,
    weekScope,
  ])

  const value = useMemo(() => {
    const totalHours = Object.values(
      employeeQuery.data?.workSchedule ?? {},
    ).reduce((sum, hoursPerDay) => sum + parseFloat(hoursPerDay), 0)

    const submittableTimeCards = timeCards.filter(
      (c) => c.isValid && !c.submitted,
    )

    const submitAll = async () => {
      for (const timeCard of submittableTimeCards) {
        try {
          await timeCard.submit()
        } catch {
          /**
           * swallowing error since it's handled by each individual call to
           * `timeCard.submit` already.  We want to continue onto the next time
           * card even if a call to submit for the previous one fails
           */
        }
      }
    }

    return {
      atLeastOneTimeCardSubmittable: submittableTimeCards.length > 0,
      end,
      isLoading,
      isUpdating,
      range,
      start,
      submitAll,
      timeCards,
      totalHours,
    }
  }, [isLoading, isUpdating, employeeQuery.data, end, range, start, timeCards])

  return (
    <TimeCardsContext.Provider value={value}>
      {children}
    </TimeCardsContext.Provider>
  )
}

export const useTimeCardsContext = () => useContext(TimeCardsContext)
